import { useState } from "react";
import { motion } from "framer-motion";

import useFinanceInvestimentos from "@/hooks/useFinanceInvestimentos";
import HeroSaldoAportadoCard from "./investimentos/HeroSaldoAportadoCard";
import InvestimentosToolbar from "./investimentos/InvestimentosToolbar";
import ClasseAgregadaCard from "./investimentos/ClasseAgregadaCard";
import DistribuicaoCarteiraDonut from "./investimentos/DistribuicaoCarteiraDonut";
import InvestimentoTable from "./investimentos/InvestimentoTable";
import InvestimentoModal from "./investimentos/InvestimentoModal";
import SugestaoConcentracaoCard from "./investimentos/SugestaoConcentracaoCard";

// FIN-4 — InvestimentosTab orquestrador.
// Hierarquia: Hero → Toolbar (T6) → 3 cards classe (T6) → Donut (T7)
// → Tabela (T8) → Sugestão Nexo (T10).
//
// T5 entrega só Hero adaptativo + shell + hook. Próximas tasks
// preenchem incrementalmente.

const ROW_TRANSITION = { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] };

export default function InvestimentosTab({ onTalk }) {
  const inv = useFinanceInvestimentos();
  const [modalState, setModalState] = useState({ open: false, investment: null });

  const openCreate = () => setModalState({ open: true, investment: null });
  const openEdit = (item) => setModalState({ open: true, investment: item });
  const closeModal = () => setModalState({ open: false, investment: null });

  const byClass = inv.summary?.by_class || {};

  return (
    <div className="space-y-5 md:space-y-6" data-testid="finance-investimentos">
      {/* Hero saldo aportado adaptativo */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={ROW_TRANSITION}
      >
        <HeroSaldoAportadoCard
          summary={inv.summary}
          isOpenFinance={inv.isOpenFinance}
          loading={inv.isLoading}
        />
      </motion.section>

      {/* Toolbar com filter pills + ações */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.08 }}
      >
        <InvestimentosToolbar
          classFilter={inv.classFilter}
          onChangeClass={inv.setClassFilter}
          exportCsv={inv.exportCsv}
          onCreate={openCreate}
          visibleCount={inv.filteredInvestments.length}
          totalCount={inv.investments.length}
        />
      </motion.section>

      {/* 3 cards classe agregada (Renda Fixa, Renda Variável, Cripto) */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.14 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5"
        data-testid="invest-class-cards"
      >
        <ClasseAgregadaCard classLabel="Renda Fixa" data={byClass["Renda Fixa"]} />
        <ClasseAgregadaCard classLabel="Renda Variável" data={byClass["Renda Variável"]} />
        <ClasseAgregadaCard classLabel="Cripto" data={byClass["Cripto"]} />
      </motion.section>

      {/* Donut distribuição */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.20 }}
      >
        <DistribuicaoCarteiraDonut byClass={inv.byClass} />
      </motion.section>
      {/* Tabela detalhada com hover actions */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.26 }}
      >
        <InvestimentoTable
          investments={inv.filteredInvestments}
          isLoading={inv.isLoading}
          hasActiveFilter={inv.classFilter && inv.classFilter !== "Todos"}
          onCreate={openCreate}
          onTalk={(item) => {
            if (!onTalk) return;
            const gainStr = Number(item.gain_loss || 0).toLocaleString("pt-BR", {
              style: "currency", currency: "BRL",
            });
            const tickerPart = item.ticker ? `(${item.ticker})` : "";
            const prompt = `O que você sabe sobre ${item.name} ${tickerPart}? ` +
              `Vale a pena manter ou aumentar posição? Está com ganho/perda de ${gainStr} ` +
              `(${Number(item.gain_loss_pct || 0).toFixed(2)}%).`;
            onTalk(prompt);
          }}
          onEdit={openEdit}
          onDelete={async (item) => {
            const tickerPart = item.ticker ? ` (${item.ticker})` : "";
            const qtyPart = item.quantity ? `${item.quantity.toLocaleString("pt-BR")} unidade(s)` : "";
            const msg = `Apagar "${item.name}${tickerPart}"${qtyPart ? `, ${qtyPart}` : ""}? Essa ação não pode ser desfeita.`;
            if (window.confirm(msg)) {
              try { await inv.deleteInvestment(item.id); } catch (_) { /* hook trata */ }
            }
          }}
          onUpdateCurrent={async (id, newValue) => {
            try { await inv.updateInvestment(id, { current_value: newValue }); }
            catch (e) { /* hook trata; rollback via fetchAll */ }
          }}
        />
      </motion.section>
      {/* T9 — Modal Adicionar/Editar */}
      {/* Sugestão Nexo de concentração (renderiza só se is_concentrated) */}
      {inv.concentrationWarning?.is_concentrated && (
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...ROW_TRANSITION, delay: 0.32 }}
        >
          <SugestaoConcentracaoCard warning={inv.concentrationWarning} onTalk={onTalk} />
        </motion.section>
      )}

      {inv.error && (
        <div
          className="text-caption text-status-error px-2"
          data-testid="invest-error"
        >
          {inv.error}
        </div>
      )}

      {/* Modal Add/Edit controlled */}
      <InvestimentoModal
        open={modalState.open}
        onOpenChange={(o) => { if (!o) closeModal(); }}
        investment={modalState.investment}
        onCreate={inv.createInvestment}
        onUpdate={inv.updateInvestment}
        knownInstitutions={inv.knownInstitutions}
      />
    </div>
  );
}
